import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

export interface IMultiLineChartProps {
    data: any;
    width?: number;
    height?: number;
    margin?: { top: number, right: number, bottom: number, left: number };
    yAxisData: any;
    xAxisData: any;
    colors?: string[];
    line?: boolean;
    yAxisLabelType?: 'inPercent' | 'inValue' | 'custom';
    customLabelFormatter?: (value: number) => string;
    currency?: string
}
const formatNumber = (num: number, symbol: string, labelType: string) => {
    if (labelType === 'inPercent') {
        return `${d3.format(".0%")(num / 100)}`;
    } else if (labelType === 'inValue') {
        return `${symbol||''} ${d3.format(".2s")(num).replace('k', 'K')}`;
    }
    return num.toString();
};



const MultiLineChart = ({
    data,
    yAxisData,
    xAxisData,
    colors = ['steelblue', 'orange', 'green', 'red'], // Default colors
    height = 400,
    margin = { top: 20, right: 50, bottom: 30, left: 50 },
    line = false,
    yAxisLabelType = 'inValue',
    currency,
    customLabelFormatter,
}: IMultiLineChartProps) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    useEffect(() => {
    }, [yAxisLabelType]);
    // ResizeObserver to adjust the chart size dynamically
    useEffect(() => {
        const wrapper = wrapperRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const newWidth = entries[0].contentRect.width;
                setContainerWidth(newWidth);
            }
        });

        if (wrapper) {
            resizeObserver.observe(wrapper);
        }

        return () => {
            if (wrapper) {
                resizeObserver.unobserve(wrapper);
            }
        };
    }, []);

    useEffect(() => {
      
        if (containerWidth === 0) return; 

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove(); 

        const innerWidth = containerWidth-margin.left- margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        // Scales
        const x = d3.scaleLinear()
            .domain([0, xAxisData.length]) // Linear scale based on index of xAxisData
            .range([0, innerWidth]);

        const y = d3.scaleLinear()
            .domain([0, d3.max(yAxisData) as number])
            .nice()
            .range([innerHeight, 0]);


        // Append x-axis
        const xAxis = svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(${margin.left},${height - margin.bottom})`)
            .call(d3.axisBottom(x)
                .ticks(xAxisData.length - 1)
                .tickFormat((d, i) => xAxisData[i])
            );

        xAxis.selectAll("text")
            .attr("transform", "translate(20, 10)") // Push labels down by 10px
            .style("text-anchor", "middle"); // Keep the labels centered
        xAxis.select(".domain").attr("stroke", "none");

        // Append y-axis
        const yAxis = svg.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${margin.left},${margin.top})`)
            .call(d3.axisLeft(y).tickPadding(10).tickFormat((d: any) =>
                customLabelFormatter
                    ? customLabelFormatter(d)
                    : formatNumber(d, currency, yAxisLabelType)
            )
                .tickSizeOuter(0) as any,);

        if (line) {
            yAxis.selectAll('.grid-line')
                .data(y.ticks())
                .enter()
                .append('line')
                .attr('class', 'grid-line')
                .attr('x1', 0)
                .attr('x2', innerWidth)
                .attr('y1', d => y(d))
                .attr('y2', d => y(d))
                .style('stroke', '#ddd')
                .style('stroke-dasharray', '6,3') // Dotted line
                .style('stroke-width', 1);
        }
        xAxis.selectAll(".tick line").remove();
        yAxis.selectAll(".tick line").remove();
        yAxis.select(".domain").attr("stroke", "none");

        // Plot the lines for each dataset

        Object.keys(data).forEach((key, index) => {
            if (key !== 'months') {
                const chartLine = d3.line()
                    .x((d, i) => x(i)) // Use index-based positioning for x-axis
                    .y((d: any) => y(d)).curve(data[key].isSmooth ? d3.curveMonotoneX : d3.curveLinear);
                // Append the line
                svg.append("path")
                    .datum(data[key]?.data)
                    .attr("fill", "none")
                    .attr("stroke", colors[index % colors.length])
                    .attr("stroke-width", 2)
                    .attr("transform", `translate(${margin.left},${margin.top})`)
                    .attr("d", chartLine)
                    .on("mouseover", function () {
                        d3.select(this).attr("stroke-width", 4);
                    })
                    .on("mouseout", function () {
                        d3.select(this).attr("stroke-width", 2);
                    });



                // Append circles for each data point
                if (data[key]?.displayDot) {
                    svg.selectAll(`.circle-${key}`)
                        .data(data[key]?.data)
                        .enter()
                        .append("circle")
                        .attr("class", `circle-${key}`)
                        .attr("cx", (d, i) => x(i) + margin.left) // Adjust for margin.left
                        .attr("cy", (d: any) => y(d) + margin.top) // Adjust for margin.top
                        .attr("r", 4) // Circle radius
                        .attr("fill", colors[index % colors.length])
                        .attr("stroke", "white") // Optional: adds a border for contrast
                        .attr("stroke-width", 1.5)
                        .on("mouseover", function () {
                            d3.select(this).attr("r", 6); // Increase size on hover
                        })
                        .on("mouseout", function () {
                            d3.select(this).attr("r", 4); // Return to normal size
                        });
                }

            }
        });
    }, [data, yAxisData, xAxisData, height, containerWidth, colors, margin, line, yAxisLabelType, currency, customLabelFormatter]);

    return (
        <div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
            <div style={{ position: 'relative' }}>
                <svg ref={svgRef} width="100%" height={height} viewBox={`0 0 ${containerWidth} ${height}`} />
            </div>
        </div>
    );
};

export { MultiLineChart };
export default MultiLineChart;
